// The review projection of the client application (SEP-263, slice 7).
//
// ONE MODULE SERVES THREE READERS. The client reads it before they submit
// (SEP-263.17). The immutable snapshot stores it (SEP-263.20). The investment
// manager reads it on Review Client Application (SEP-263.25). Three readers of
// one projection can never disagree about what the client answered.
//
// # THE BANK DRIVES IT
//
// Every label, option and explainer comes from
// `client-application-questions.ts`. This module retypes no question and no
// copy of the firm. It renders an ANSWER, and nothing else.
//
// # PART 11 NEVER REACHES STAFF
//
// A question that carries `excludeFromStaffReview` is a bank-grade identifier.
// {@link clientApplicationReview} drops it when the reader is staff, and it
// keeps it for the client, who is reviewing their own answers. The staff
// projection therefore cannot print a security answer, because the answer is
// not in the value it reads.
//
// # THE VERSION OF THE FORM GIVES THE WORDS
//
// A snapshot records the version of the form that the client filled in
// (SEP-293.3). A reader gives that version in the context, so each answer
// shows with the words and under the conditions of that version. A question
// that a later version retired still shows in an older snapshot. A draft and
// the client review use the current version, which is the default.

import {
    CLIENT_APPLICATION_NOT_APPLICABLE_LABEL,
    clientApplicationContextBank,
    clientApplicationContextVersion,
    clientApplicationNotApplicableId,
    clientApplicationSections,
    isClientApplicationQuestionRequired,
    type ClientApplicationFormContext,
    type ClientApplicationQuestion,
    type ClientApplicationSection,
} from './client-application-questions';
import {
    isBlankClientApplicationAnswer,
    upgradeClientApplicationAnswers,
    visibleClientApplicationQuestions,
    type ClientApplicationAnswer,
    type ClientApplicationAnswers,
} from './client-application-validation';
import { clientApplicationStaffBlankDisplay } from './client-application-blank-answer';
import { countryName } from './countries';

/** Who reads the projection. Staff never see a Part 11 answer. */
export type ClientApplicationReviewAudience = 'client' | 'staff';

/** One answered question, as a reader reads it. */
export interface ClientApplicationReviewAnswer {
    readonly questionId: string;
    readonly label: string;
    /** The form's own sub-heading, where the question carries one. */
    readonly group: string | null;
    /** The answer as text. A money answer already carries the pound sign. */
    readonly display: string;
    /** True when the question is required and the answer is blank. */
    readonly missing: boolean;
}

/** One section of the review, in the order the form prints it. */
export interface ClientApplicationReviewSection {
    readonly sectionId: string;
    readonly part: number | null;
    readonly title: string;
    readonly answers: readonly ClientApplicationReviewAnswer[];
    /** True when every visible required question of the section is answered. */
    readonly complete: boolean;
}

/**
 * How a caller renders a money answer.
 *
 * THE CONTRACT DOES NOT FORMAT MONEY. Decision D10 of SEP-243 gives the repo
 * ONE money shape, and it lives in `apps/web/src/lib/format.ts` with its
 * DOM-free twin in `apps/server/src/lib/format-currency.ts`. This package can
 * import neither, so a money literal here would be a SECOND shape that drifts
 * from the first, which is the defect the money-format guard exists to catch.
 * The caller therefore passes its own home formatter.
 */
export type ClientApplicationMoneyFormatter = (amount: number) => string;

/** What the projection prints for a blank answer. A client surface may print nothing instead. */
export const CLIENT_APPLICATION_BLANK_DISPLAY = '—';

/**
 * One answer as text.
 *
 * A blank answer reads as an em-free dash, because a reader must see that the
 * question was asked and left empty. A country code becomes the name of the
 * country, a date reads in the British order, and a tick becomes the word
 * the form binds it to.
 */
export function formatClientApplicationAnswer(
    question: ClientApplicationQuestion,
    value: ClientApplicationAnswer,
    formatMoney: ClientApplicationMoneyFormatter,
): string {
    if (isBlankClientApplicationAnswer(value)) return CLIENT_APPLICATION_BLANK_DISPLAY;
    if (question.kind === 'acknowledgement') return 'Accepted';
    if (question.kind === 'tick') return 'Yes';
    if (question.kind === 'currency' && typeof value === 'number') {
        return formatMoney(value);
    }
    if (question.kind === 'number' && typeof value === 'number') {
        return `${value.toLocaleString('en-GB')}${question.suffix ?? ''}`;
    }
    if (question.kind === 'date' && typeof value === 'string') {
        // The stored day is ISO. A reader reads the British order (SEP-293).
        const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
        return day ? `${day[3]}/${day[2]}/${day[1]}` : value;
    }
    if (question.kind === 'country_select' && typeof value === 'string') {
        return countryName(value) ?? value;
    }
    if (Array.isArray(value)) return value.join(', ');
    return String(value);
}

/** The questions of one section this audience may read. */
function readableQuestions(
    section: ClientApplicationSection,
    answers: ClientApplicationAnswers,
    audience: ClientApplicationReviewAudience,
    context: ClientApplicationFormContext,
): readonly ClientApplicationQuestion[] {
    // A "Not applicable" tick prints on the row of its question, not as a row.
    const visible = visibleClientApplicationQuestions(section, answers, context).filter(
        (question) => question.notApplicableFor === undefined,
    );
    if (audience === 'client') return visible;
    return visible.filter((question) => question.excludeFromStaffReview !== true);
}

/**
 * The whole application as a reader reads it.
 *
 * Every VISIBLE question is present, answered or not, because the client must
 * see what they left blank before they submit. A section with no readable
 * question is dropped, so a staff reader never meets an empty Part 11 heading.
 *
 * The context gives the version of the form (the default is the current
 * version) and the date of today. A reader of a snapshot gives the version
 * that the snapshot records and the date of submission.
 */
export function clientApplicationReview(
    stored: ClientApplicationAnswers,
    audience: ClientApplicationReviewAudience,
    formatMoney: ClientApplicationMoneyFormatter,
    context: ClientApplicationFormContext = {},
): readonly ClientApplicationReviewSection[] {
    const version = clientApplicationContextVersion(context);
    const bank = clientApplicationContextBank(context);
    const resolved: ClientApplicationFormContext = { ...context, version, bank };
    // A map an older bank saved reads in the shape that its version holds.
    const answers = upgradeClientApplicationAnswers(stored, version, { form: context.form });
    const sections: ClientApplicationReviewSection[] = [];
    for (const section of clientApplicationSections(version, bank)) {
        const questions = readableQuestions(section, answers, audience, resolved);
        if (questions.length === 0) continue;
        const rows = questions.map((question): ClientApplicationReviewAnswer => {
            const value = answers[question.id] ?? null;
            const blank = isBlankClientApplicationAnswer(value);
            const notApplicable =
                question.notApplicable === true &&
                answers[clientApplicationNotApplicableId(question.id)] === true;
            // Only the client sees what is missing, before they submit. The
            // server refuses a submission with a blank required answer, so a
            // snapshot holds none. A snapshot from before each answer was
            // required can hold a blank, and a staff reader must not see it as
            // missing (decision of 22 October 2026: each answer is required).
            const missing =
                audience === 'client' &&
                isClientApplicationQuestionRequired(question, answers) &&
                blank;
            // A staff reader sees a blank answer as `0` or `-`, so the
            // application reads as complete (SEP-318).
            const display = notApplicable
                ? CLIENT_APPLICATION_NOT_APPLICABLE_LABEL
                : audience === 'staff' && blank
                  ? clientApplicationStaffBlankDisplay(question, formatMoney)
                  : formatClientApplicationAnswer(question, value, formatMoney);
            return {
                questionId: question.id,
                label: question.label,
                group: question.group ?? null,
                display,
                missing,
            };
        });
        sections.push({
            sectionId: section.id,
            part: section.part,
            title: section.title,
            answers: rows,
            complete: rows.every((row) => !row.missing),
        });
    }
    return sections;
}

/**
 * The answers a snapshot stores, with Part 11 held apart (decision D4).
 *
 * The security answers are ordinary answers of the form, and they are also
 * bank-grade identifiers. They therefore sit in their own map rather than in
 * the general snapshot, so a reader of the snapshot cannot reach one by
 * accident.
 */
export interface ClientApplicationSnapshotAnswers {
    /** Every answer except the Part 11 security answers. */
    readonly answers: Readonly<Record<string, ClientApplicationAnswer>>;
    /** The Part 11 security answers, keyed by question id. */
    readonly securityAnswers: Readonly<Record<string, ClientApplicationAnswer>>;
}

/**
 * Split the answers of one application into the general map and Part 11.
 *
 * It keeps only the ids that the version holds (the default is the current
 * version, which is the version of each new submission).
 */
export function splitClientApplicationAnswers(
    answers: ClientApplicationAnswers,
    context: Pick<ClientApplicationFormContext, 'version' | 'bank' | 'form'> = {},
): ClientApplicationSnapshotAnswers {
    const general: Record<string, ClientApplicationAnswer> = {};
    const security: Record<string, ClientApplicationAnswer> = {};
    const sections = clientApplicationSections(
        clientApplicationContextVersion(context),
        clientApplicationContextBank(context),
    );
    for (const section of sections) {
        for (const question of section.questions) {
            const value = answers[question.id];
            if (value === undefined) continue;
            if (question.excludeFromStaffReview === true) security[question.id] = value;
            else general[question.id] = value;
        }
    }
    return { answers: general, securityAnswers: security };
}

// ---------------------------------------------------------------------------
// The key facts (SEP-263.25, slice 8)
// ---------------------------------------------------------------------------

/**
 * The answers the investment manager reads FIRST.
 *
 * The firm decided that the investment manager is the person who judges
 * whether an application is genuine, so the review screen gives them the facts
 * at the top and the whole form below. This list is a PRESENTATION choice and
 * not a rule of the domain: it says which answers a reader wants first, and it
 * invents no score and no risk rating.
 *
 * Each id is a frozen question id of the bank, and a guard test fails the
 * build when one of them leaves the bank. The label and the wording still come
 * from the bank, so no copy of the firm is retyped here.
 *
 * NO PART 11 ANSWER MAY JOIN THIS LIST. A question that carries
 * `excludeFromStaffReview` is a bank-grade identifier, and
 * {@link clientApplicationKeyFacts} drops one that somebody adds by mistake.
 */
export const CLIENT_APPLICATION_KEY_FACT_IDS: readonly string[] = [
    'employment_status',
    'job_title',
    'income_employment',
    'income_all_other',
    'source_types',
    'asset_cash_savings_premium_bonds',
    'asset_other_investments_value',
    'owns_or_holds_significant_share_in_business',
    'account_types',
    // A submission of form version 2.0 holds the first, and a submission of
    // version 3.0 holds the second (SEP-293.1).
    'investment_objective',
    'current_investment_objective',
    'intended_risk_profile',
    // SEP-293.8: whether the client appoints a representative, and of which
    // types. The details of the representative are in the review of Part 10.
    'appoint_nominated_representative',
    'nominated_representative_types',
];

/** One fact of the band, as a reader reads it. */
export interface ClientApplicationKeyFact {
    readonly questionId: string;
    readonly label: string;
    readonly display: string;
    /** True for a money answer, which a surface prints in a numeric column. */
    readonly numeric: boolean;
}

/**
 * The key facts of one application, in the order the list states them.
 *
 * A question the client never saw, because a condition of the bank hid it, is
 * dropped rather than printed blank: an answer that was never asked for is not
 * a gap in the application.
 *
 * The context gives the version of the form, as for
 * {@link clientApplicationReview}. A fact that the version does not hold is
 * dropped.
 */
export function clientApplicationKeyFacts(
    stored: ClientApplicationAnswers,
    formatMoney: ClientApplicationMoneyFormatter,
    context: Pick<ClientApplicationFormContext, 'version' | 'bank' | 'form'> = {},
): readonly ClientApplicationKeyFact[] {
    const version = clientApplicationContextVersion(context);
    const answers = upgradeClientApplicationAnswers(stored, version, { form: context.form });
    const byId = new Map(
        clientApplicationSections(version, clientApplicationContextBank(context))
            .flatMap((section) => section.questions)
            .map((question) => [question.id, question]),
    );
    const facts: ClientApplicationKeyFact[] = [];
    // Each form names its own key facts (SEP-293).
    for (const id of context.form?.keyFactIds ?? CLIENT_APPLICATION_KEY_FACT_IDS) {
        const question = byId.get(id);
        if (!question) continue;
        if (question.excludeFromStaffReview === true) continue;
        const value = answers[id] ?? null;
        if (isBlankClientApplicationAnswer(value)) continue;
        facts.push({
            questionId: id,
            label: question.label,
            display: formatClientApplicationAnswer(question, value, formatMoney),
            numeric: question.kind === 'currency' || question.kind === 'number',
        });
    }
    return facts;
}
